import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../services/analytics';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const GENERIC_OAUTH_ERROR = 'Social login failed. Please try again or use email and password.';

// Backend redirects here as /login?error=<code>[&provider=<local|google|microsoft>].
// Anything not listed falls back to the generic message — never render a raw code.
const EMAIL_IN_USE_BY_PROVIDER: Record<string, string> = {
  local: 'An account with this email already exists. Sign in with your email and password below.',
  google: 'This email is already registered with Google. Use the "Continue with Google" button above.',
  microsoft: 'This email is already registered with Microsoft. Use the "Continue with Microsoft" button above.',
};
const EMAIL_IN_USE_FALLBACK =
  'An account with this email already exists. Try signing in with the method you used originally.';

// Failures of the "connect a Google/Microsoft account" flow started from the dashboard. The
// OAuth callback has no way back to the dashboard, so it lands here — say what happened rather
// than dead-ending on the generic message.
const LINK_ERROR_MESSAGES: Record<string, string> = {
  link_expired:
    'That connection request expired or was not valid, so nothing was changed. Sign in and start again from your dashboard.',
  link_failed: 'We could not connect that account. Sign in and try again from your dashboard.',
  already_linked:
    'Your account already has a Google or Microsoft account connected, and only one can be connected at a time. Nothing was changed.',
  identity_in_use:
    'That Google or Microsoft account is already used to sign in to a different account here. Nothing was changed.',
};

function oauthErrorMessage(code: string, provider: string | null): string {
  if (code === 'email_in_use') {
    return (provider && EMAIL_IN_USE_BY_PROVIDER[provider]) || EMAIL_IN_USE_FALLBACK;
  }
  return LINK_ERROR_MESSAGES[code] || GENERIC_OAUTH_ERROR;
}

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // Marketing-channel deep links can open the form pre-flipped to register via ?mode=register.
  const [isRegistering, setIsRegistering] = useState(() => searchParams.get('mode') === 'register');
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { userId: urlUserId } = useParams<{ userId?: string }>();
  const signupSource = searchParams.get('source') || undefined;

  useEffect(() => {
    const oauthError = searchParams.get('error');
    if (oauthError) {
      setError(oauthErrorMessage(oauthError, searchParams.get('provider')));
    }
  }, [searchParams]);

  useEffect(() => {
    if (isRegistering) {
      track('signup_view', { source: signupSource || null });
    }
  }, [isRegistering, signupSource]);

  const toggleMode = () => {
    setIsRegistering(prev => !prev);
    setError('');
    setCaptchaToken('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (isRegistering) {
        await register(identifier, password, captchaToken, signupSource);
      } else {
        await login(identifier, password);
      }
      // Use a setTimeout to ensure userId is updated in the context
      setTimeout(() => {
        // Prioritize URL userId if present, then auth userId from localStorage
        const targetUserId = urlUserId || localStorage.getItem('userId');
        if (targetUserId) {
          navigate(`/${targetUserId}/admin`);
        } else {
          navigate('/admin'); // fallback to legacy route
        }
      }, 100);
    } catch (err: any) {
      const data = err.response?.data;
      const detailMessage = Array.isArray(data?.details) && data.details.length > 0
        ? data.details.map((d: { message: string }) => d.message).join('. ')
        : null;
      setError(detailMessage || data?.error || 'An error occurred');
      if (isRegistering) {
        setCaptchaToken('');
        turnstileRef.current?.reset();
      }
    }
  };

  const submitDisabled = isRegistering && !captchaToken;

  return (
    <Container className="mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <Card>
            <Card.Body>
              <h2 className="text-center mb-2">
                {isRegistering ? 'Register' : 'Login'}
              </h2>
              <p className="text-center text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                Need help? Email{' '}
                <a href="mailto:info@mapofprojects.com">info@mapofprojects.com</a>
              </p>
              {error && <Alert variant="danger">{error}</Alert>}

              {/* Social Login Buttons */}
              <div className="d-grid gap-2 mb-3">
                <a
                  href={`${API_BASE_URL}/auth/google`}
                  className="btn btn-outline-dark d-flex align-items-center justify-content-center gap-2"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                  Continue with Google
                </a>
                <a
                  href={`${API_BASE_URL}/auth/microsoft`}
                  className="btn btn-outline-dark d-flex align-items-center justify-content-center gap-2"
                >
                  <svg width="18" height="18" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
                  Continue with Microsoft
                </a>
              </div>

              <div className="text-center text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                <hr className="my-2" />
                <span style={{ position: 'relative', top: '-0.75rem', background: '#fff', padding: '0 0.75rem' }}>
                  or continue with email
                </span>
              </div>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>{isRegistering ? 'Email' : 'Email or username'}</Form.Label>
                  <Form.Control
                    type={isRegistering ? 'email' : 'text'}
                    autoComplete={isRegistering ? 'email' : 'username'}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    autoComplete={isRegistering ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={isRegistering ? 8 : undefined}
                    required
                  />
                  {isRegistering && (
                    <Form.Text className="text-muted">
                      At least 8 characters.
                    </Form.Text>
                  )}
                </Form.Group>
                {isRegistering && TURNSTILE_SITE_KEY && (
                  <div className="mb-3 d-flex justify-content-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={setCaptchaToken}
                      onError={() => setCaptchaToken('')}
                      onExpire={() => setCaptchaToken('')}
                      options={{ theme: 'light' }}
                    />
                  </div>
                )}
                <Button variant="primary" type="submit" className="w-100" disabled={submitDisabled}>
                  {isRegistering ? 'Register' : 'Login'}
                </Button>
              </Form>
              <div className="text-center mt-3">
                <Button
                  variant="link"
                  onClick={toggleMode}
                >
                  {isRegistering
                    ? 'Already have an account? Login'
                    : "Don't have an account? Register"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default Login;
